import { useTranslation } from '@wix/yoshi-flow-editor';
import { useSettings } from '@wix/tpa-settings/react';

import { DispatchType } from '../../../types/businessTypes';
import { formatPrice } from '../../../utils/price';
import {
  StatusBadgeIndicatorType,
  type StatusBadgeProps,
} from '../../../components-shared/StatusBadge';
import type { HeaderProps } from '../components/Header';
import { useOnlineOrdersStorage } from '../storage';
import settingsParams from '../settingsParams';
import { RequestStatus } from '../../../utils/wrapRequest';

export const useHeaderProps = (): HeaderProps => {
  const { t } = useTranslation();
  const settings = useSettings();
  const {
    currentOperation,
    operations,
    fulfillmentMethods,
    selectedDispatchType,
    dispatchesInfo,
    acceptingDispatchTypesByOperationId,
    fetchTimeSlotsStatus,
    fetchFulfillmentMethodsStatus,
    currency,
    isLocationSelected,
    openFulfillmentModal,
    coverImage,
  } = useOnlineOrdersStorage();

  const hasAvailableSlot = Object.values(dispatchesInfo).some(
    (info) => !!info?.selectedTimeSlot,
  );
  const isAcceptingOrders =
    (currentOperation?.enabled ?? false) && hasAvailableSlot;
  const isStatusBarReady =
    fetchTimeSlotsStatus === RequestStatus.RESOLVED &&
    fetchFulfillmentMethodsStatus === RequestStatus.RESOLVED;
  const isMultiLocation = operations.length > 1;
  const isGhostMode = isMultiLocation && !isLocationSelected;
  const shouldShowLocationButton =
    isMultiLocation && !isGhostMode && !!currentOperation;

  const selectedFulfillment = selectedDispatchType
    ? fulfillmentMethods.find(
        (m) => (m.type as string) === selectedDispatchType && m.enabled,
      )
    : undefined;

  const additionalBadges: StatusBadgeProps[] = [];

  const minOrder = selectedFulfillment?.minOrderPrice;

  if (minOrder && Number(minOrder) > 0) {
    additionalBadges.push({
      indicator: StatusBadgeIndicatorType.NONE,
      text: t('header_olo.minOrder.exact', {
        amount: formatPrice(Number(minOrder), currency),
      }),
    });
  }

  if (selectedDispatchType === DispatchType.DELIVERY) {
    const fee = selectedFulfillment?.fee;

    if (fee !== null && fee !== undefined) {
      if (Number(fee) === 0) {
        additionalBadges.push({
          indicator: StatusBadgeIndicatorType.NONE,
          text: t('header_olo.deliveryFee.free'),
        });
      } else {
        additionalBadges.push({
          indicator: StatusBadgeIndicatorType.NONE,
          text: t('header_olo.deliveryFee.exact', {
            amount: formatPrice(Number(fee), currency),
          }),
        });

        const freeThreshold =
          selectedFulfillment?.deliveryOptions?.freeDeliveryThreshold;

        if (freeThreshold && Number(freeThreshold) > 0) {
          additionalBadges.push({
            indicator: StatusBadgeIndicatorType.NONE,
            text: t('header_olo.deliveryFee.freeAbove', {
              amount: formatPrice(Number(freeThreshold), currency),
            }),
          });
        }
      }
    }
  }

  const showTitle = settings.get(settingsParams.showHeaderTitle);
  const showDescription = settings.get(settingsParams.showHeaderDescription);
  const customTitle = settings.get(settingsParams.headerTitle);
  const customDescription = settings.get(settingsParams.headerDescription);

  return {
    title: showTitle ? customTitle || t('header_olo.default.title') : '',
    description: showDescription
      ? customDescription || t('header_olo.default.description')
      : '',
    statusBarData:
      isStatusBarReady && !isGhostMode
        ? [
            {
              indicator: isAcceptingOrders
                ? StatusBadgeIndicatorType.ON
                : StatusBadgeIndicatorType.OFF,
              text: isAcceptingOrders
                ? t('header_olo.status.AcceptingOrders')
                : t('header_olo.status.NotAcceptingOrders'),
            },
            ...additionalBadges,
          ]
        : [],
    locationName: shouldShowLocationButton
      ? currentOperation?.locationDetails?.name ?? undefined
      : undefined,
    showOrderNow: isGhostMode,
    onLocationButtonClick: shouldShowLocationButton
      ? () =>
          openFulfillmentModal({
            operations,
            currentOperation,
            dispatchesInfo,
            selectedDispatchType,
            acceptingDispatchTypesByOperationId,
          })
      : undefined,
    coverImage,
  };
};
